import { useCallback, useEffect, useState } from 'react'

type SizeOptions = {
  box?: 'border-box' | 'content-box'
  fallbackToContentRect?: boolean
}

export const useResize = <T extends HTMLElement>(
  options: SizeOptions = {
    box: 'border-box',
    fallbackToContentRect: true,
  }
) => {
  const [element, setElement] = useState<T | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  const ref = useCallback((node: T | null) => {
    if (node !== null) {
      setElement(node)
    }
  }, [])

  useEffect(() => {
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (options.box === 'border-box' && entry.borderBoxSize?.[0]) {
        const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0]
        setSize({ width, height })
      } else if (options.box === 'content-box' && entry.contentBoxSize?.[0]) {
        const { inlineSize: width, blockSize: height } = entry.contentBoxSize[0]
        setSize({ width, height })
      } else if (options.fallbackToContentRect) {
        const { width, height } = entry.contentRect
        setSize({ width, height })
      }
    })

    const rect = element.getBoundingClientRect()
    setSize({ width: rect.width, height: rect.height })

    observer.observe(element, { box: options.box })

    return () => {
      observer.disconnect()
    }
  }, [element, options.box, options.fallbackToContentRect])

  return { ref, size }
}
